// RotationView — schedule preview for every rotating chore in the
// household (adult and kid). Read-only at first; shows who owns the
// chore this week, next, and +2 so adults can see the upcoming
// roster at a glance without digging into each chore.
//
// Single-mode and parallel-mode chores never appear here — by
// definition they don't rotate. If there are no rotating chores at
// all, render an empty-state nudge.

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  api,
  type AdultChore,
  type FamilyStarsMember,
  type UnifiedChore,
} from "../../lib/rpc";

interface Props {
  refreshToken: number;
}

interface RotationRow {
  key: string;
  name: string;
  icon: string | null;
  kind: "kid" | "adult";
  /** This week, next week, +2. Resolved owner per-week (or null when
   *  the roster collapses — e.g. adult rotating with zero adults). */
  owners: Array<{ weekLabel: string; ownerId: string | null }>;
}

function mondayOfWeek(dateIso: string): string {
  const d = new Date(`${dateIso}T00:00:00Z`);
  const shift = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - shift);
  return d.toISOString().slice(0, 10);
}
function addWeeks(dateIso: string, n: number): string {
  const d = new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n * 7);
  return d.toISOString().slice(0, 10);
}
function weekIndex(dateIso: string): number {
  const monday = mondayOfWeek(dateIso);
  const epochMonday = new Date("1970-01-05T00:00:00Z").getTime();
  const d = new Date(`${monday}T00:00:00Z`).getTime();
  return Math.floor((d - epochMonday) / (7 * 86_400_000));
}
function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

export function RotationView({ refreshToken }: Props) {
  const [unified, setUnified] = useState<UnifiedChore[] | null>(null);
  const [adultChores, setAdultChores] = useState<AdultChore[] | null>(null);
  const [members, setMembers] = useState<FamilyStarsMember[] | null>(null);
  // Adult rotating chores use the household's adult-member join order
  // as their pool. We have to fetch members to resolve that.

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api.choresAllForHousehold().catch(() => ({ chores: [] as UnifiedChore[], today: "" })),
      api.adultChoresList().catch(() => [] as AdultChore[]),
      api.householdsStarsByMember().catch(() => [] as FamilyStarsMember[]),
    ]).then(([cr, ac, m]) => {
      if (cancelled) return;
      setUnified(cr.chores || []);
      setAdultChores(ac);
      setMembers(m);
    });
    return () => { cancelled = true; };
  }, [refreshToken]);

  const rows: RotationRow[] = useMemo(() => {
    if (!unified || !adultChores || !members) return [];
    const today = new Date().toISOString().slice(0, 10);
    const monday0 = mondayOfWeek(today);
    const week0 = weekIndex(today);
    const labelFor = (offset: number) =>
      offset === 0 ? "this week"
        : offset === 1 ? "next week"
          : `wk of ${shortDate(addWeeks(monday0, offset))}`;

    const out: RotationRow[] = [];

    // Adult rotating chores — pool is the household's adult-member
    // join order (kids excluded), same predicate the server uses.
    const adultPool = members
      .filter((m) => !m.isKid)
      .map((m) => m.userId);
    for (const c of adultChores) {
      if (c.assignmentMode !== "rotating") continue;
      const pool = adultPool;
      out.push({
        key: `adult:${c.id}`,
        name: c.name,
        icon: c.icon || null,
        kind: "adult",
        owners: [0, 1, 2].map((off) => ({
          weekLabel: labelFor(off),
          ownerId: pool.length === 0
            ? null
            : pool[(week0 + off) % pool.length],
        })),
      });
    }

    // Kid rotating chores — roster is the chore's `assignees` list
    // (ordered). Same week-parity math, just with a per-chore pool.
    for (const c of unified) {
      if (c.kind !== "kid" || c.assignmentMode !== "rotating") continue;
      const pool = c.assignees;
      out.push({
        key: c.id,
        name: c.name,
        icon: c.icon || null,
        kind: "kid",
        owners: [0, 1, 2].map((off) => ({
          weekLabel: labelFor(off),
          ownerId: pool.length === 0
            ? null
            : pool[(week0 + off) % pool.length],
        })),
      });
    }

    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [unified, adultChores, members]);

  if (unified === null || adultChores === null || members === null) {
    return <div className="rotation-loading">Loading…</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="chores-stub" data-testid="rotation-empty">
        <p>No rotating chores yet.</p>
        <p className="chores-stub-sub">
          When you create a chore in "Rotates" mode it'll show its schedule here.
        </p>
      </div>
    );
  }

  return (
    <section className="rotation-section" data-testid="rotation-view">
      <ul className="rotation-list">
        {rows.map((row) => (
          <li key={row.key} className="rotation-card">
            <header className="rotation-card-head">
              <span className="rotation-icon" aria-hidden>{row.icon || (row.kind === "adult" ? "🔁" : "🧒")}</span>
              <div className="rotation-card-name">{row.name}</div>
              <span className="rotation-kind" data-kind={row.kind}>
                {row.kind === "adult" ? "Adult" : "Kid"}
              </span>
            </header>
            <ol className="rotation-weeks">
              {row.owners.map((o, i) => (
                <li
                  key={o.weekLabel}
                  className="rotation-week"
                  data-current={i === 0}
                  data-testid={`rotation-${row.key}-week-${i}`}
                >
                  <span className="rotation-week-label">{o.weekLabel}</span>
                  <span className="rotation-week-owner">
                    {o.ownerId || <em className="rotation-empty-owner">no one</em>}
                  </span>
                </li>
              ))}
            </ol>
          </li>
        ))}
      </ul>
      <p className="rotation-hint">
        Schedule is read-only for now. Edit a chore's roster to change who's next.
      </p>
    </section>
  );
}
